'use client'

import { type DragEvent, type MouseEvent, useEffect, useRef, useState } from 'react'

// React Interpolate needs declarations
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Interpolate from '@doist/react-interpolate'

import cn from 'clsx'
import { TwistUpload } from '@doist/icons/base'

import { Box } from '@/components/Box/Box'
import {
    BaseField,
    type BaseFieldLabelVariant,
    type BaseFieldProps,
} from '@/components/future/base-field/base-field'
import { Text } from '@/components/Text/Text'
import { VisuallyHidden } from '@/components/VisuallyHidden/VisuallyHidden'

import css from './UploadField.module.css'

export type UploadFieldProps = Omit<BaseFieldProps, 'action' | 'icon' | 'variant'> &
    BaseFieldLabelVariant & {
        dropLabel: string
        // If undefined or empty, accept any files. Example: ['jpg', 'png']
        acceptExtensions?: string[]
        onFilesDropped?: (files: FileList) => void
        variant?: 'small' | 'base'
    }

export function UploadField({
    id,
    acceptExtensions = [],
    onFilesDropped,
    variant = 'base',
    dropLabel = 'Drop image or <browse>browse</browse>',
    ...rest
}: UploadFieldProps) {
    if (!dropLabel.includes('<browse>') || !dropLabel.includes('</browse>')) {
        throw new Error(
            'Marketist UploadField: the `dropLabel` must include a `<browse>` node in the string. See the default value for an example',
        )
    }

    const $file = useRef<HTMLInputElement>(null)

    useEffect(() => {
        const preventOpeningFile = (e: Event) => e.preventDefault()
        window.addEventListener('dragover', preventOpeningFile)
        window.addEventListener('drop', preventOpeningFile)

        return () => {
            window.removeEventListener('dragover', preventOpeningFile)
            window.removeEventListener('drop', preventOpeningFile)
        }
    }, [])

    const onInputFileChanged = async () => {
        if ($file.current) {
            const files = $file.current.files
            if (!files || files.length <= 0) return
            if (onFilesDropped) {
                onFilesDropped(files)
            }
        }
    }

    /** Drag‘n‘drop management. */
    const [isDragging, setDragging] = useState(false)
    const onFilesDrop = (event: DragEvent<HTMLDivElement>) => {
        setDragging(false)
        const files = event.dataTransfer.files
        if (files.length > 0 && onFilesDropped) {
            if (acceptExtensions.length > 0) {
                // filter files by extension
                const dt = new DataTransfer()
                for (const file of Array.from(files)) {
                    const ext = file.name.split('.').pop()
                    if (ext && acceptExtensions.includes(ext)) {
                        dt.items.add(file)
                    }
                }

                if (dt.files.length > 0) {
                    onFilesDropped(dt.files)
                }
            } else {
                onFilesDropped(files)
            }
        }
    }

    const textSize = 'body-2xs'

    return (
        <BaseField {...rest} variant="outline" id={id} draggable={true} isDragging={isDragging}>
            {({ className, ...fieldProps }) => {
                return (
                    <Box
                        tag="button"
                        type="button"
                        onClick={() => $file.current?.click()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && $file.current) {
                                $file.current.click()
                            }
                        }}
                        className={cn(className, css.uploadField, css[variant])}
                        tabIndex={0}
                        onDragEnter={() => setDragging(true)}
                        onDragLeave={() => setDragging(false)}
                        onDrop={onFilesDrop}
                        {...fieldProps}
                    >
                        <Box className={css.content}>
                            <Box className={css.icon}>
                                <TwistUpload />
                            </Box>

                            <Text size={textSize} className={css.text}>
                                <Interpolate
                                    string={dropLabel}
                                    mapping={{ browse: <Text tag="span" size={textSize} /> }}
                                />
                            </Text>
                        </Box>

                        <VisuallyHidden>
                            <input
                                ref={$file}
                                type="file"
                                data-testid="file-input"
                                onChange={onInputFileChanged}
                                onClick={(event: MouseEvent<HTMLInputElement>) => {
                                    // Reset the input value so that when uploading the same file, an onChange event will still be triggered.
                                    // https://stackoverflow.com/a/12102992
                                    // Reset the input value so that when uploading the same file, an onChange event will still be triggered.
                                    // https://stackoverflow.com/a/12102992
                                    event.currentTarget.value = ''
                                }}
                                accept={acceptExtensions.map((ext) => `.${ext}`).join(',')}
                                multiple
                                disabled={rest.disabled}
                                tabIndex={-1}
                            />
                        </VisuallyHidden>
                    </Box>
                )
            }}
        </BaseField>
    )
}
